import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import { Box, Typography, Button as MuiButton } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';
import { colors, spacing, typography, radius, transitions } from '../../theme/tokens';

const ScreenContainer = styled(Box)(({ theme }) => ({
  maxWidth: '480px',
  margin: '0 auto',
  padding: `${spacing.xl} ${spacing.md}`,
  textAlign: 'center',
  [theme.breakpoints.down('sm')]: {
    padding: spacing.md,
  },
}));

const CelebrationIcon = styled(Box)(({ theme }) => ({
  marginBottom: spacing.md,
  animation: 'bounceIn 0.6s ease-out',
  '@keyframes bounceIn': {
    '0%': {
      opacity: 0,
      transform: 'scale(0.3)',
    },
    '50%': {
      opacity: 1,
      transform: 'scale(1.05)',
    },
    '70%': {
      transform: 'scale(0.9)',
    },
    '100%': {
      transform: 'scale(1)',
    },
  },
  '& svg': {
    fontSize: '56px',
    color: colors.success.main,
  },
  [theme.breakpoints.down('sm')]: {
    marginBottom: spacing.sm,
    '& svg': {
      fontSize: '44px',
    },
  },
}));

const Title = styled(Typography)(({ theme }) => ({
  fontSize: typography.fontSize['2xl'],
  fontWeight: typography.fontWeight.bold,
  color: colors.text.primary,
  marginBottom: spacing.sm,
  lineHeight: 1.3,
  [theme.breakpoints.down('sm')]: {
    fontSize: typography.fontSize.xl,
  },
}));

const Subtitle = styled(Typography)(({ theme }) => ({
  fontSize: typography.fontSize.base,
  color: colors.text.muted,
  marginBottom: spacing.lg,
  lineHeight: 1.5,
  [theme.breakpoints.down('sm')]: {
    fontSize: typography.fontSize.sm,
    marginBottom: spacing.md,
  },
}));

const Card = styled(Box)(({ theme }) => ({
  backgroundColor: '#ffffff',
  borderRadius: radius.card,
  padding: spacing.lg,
  marginBottom: spacing.md,
  border: `1px solid ${colors.border.light}`,
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
  [theme.breakpoints.down('sm')]: {
    padding: spacing.md,
  },
}));

const PhoneInputWrapper = styled(Box)({
  marginBottom: spacing.md,
});

const PhoneLabel = styled(Typography)({
  fontSize: typography.fontSize.sm,
  fontWeight: typography.fontWeight.medium,
  color: colors.text.secondary,
  marginBottom: spacing.xs,
  textAlign: 'right',
});

const PhoneInput = styled('input')(({ theme }) => ({
  width: '100%',
  padding: `14px ${spacing.md}`,
  fontSize: typography.fontSize.lg,
  fontWeight: typography.fontWeight.medium,
  border: `1.5px solid ${colors.border.main}`,
  borderRadius: radius.input,
  backgroundColor: '#ffffff',
  textAlign: 'center',
  direction: 'ltr',
  letterSpacing: '1.5px',
  boxSizing: 'border-box',
  transition: `all ${transitions.duration.fast} ease`,
  '&:focus': {
    outline: 'none',
    borderColor: colors.primary.main,
    boxShadow: `0 0 0 3px rgba(19, 36, 161, 0.1)`,
  },
  '&::placeholder': {
    color: colors.neutral[400],
    fontWeight: typography.fontWeight.regular,
    letterSpacing: '1.5px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: typography.fontSize.base,
    padding: `12px ${spacing.md}`,
  },
}));

const SubmitButton = styled(MuiButton)(({ theme }) => ({
  backgroundColor: colors.primary.main,
  color: '#ffffff',
  fontSize: typography.fontSize.base,
  fontWeight: typography.fontWeight.semibold,
  padding: '14px 24px',
  borderRadius: radius.button,
  textTransform: 'none',
  boxShadow: '0 2px 8px rgba(19, 36, 161, 0.25)',
  transition: `all ${transitions.duration.fast} ease`,
  '&:hover': {
    backgroundColor: '#0f1d7a',
    boxShadow: '0 4px 12px rgba(19, 36, 161, 0.3)',
  },
  '&:active': {
    transform: 'translateY(1px)',
  },
  '&:disabled': {
    backgroundColor: colors.primary.main,
    color: '#ffffff',
    opacity: 0.4,
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: typography.fontSize.sm,
    padding: '12px 20px',
  },
}));

const InfoBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: spacing.sm,
  padding: spacing.md,
  backgroundColor: colors.neutral[50],
  border: `1px solid ${colors.border.light}`,
  borderRadius: radius.card,
  textAlign: 'right',
  [theme.breakpoints.down('sm')]: {
    padding: spacing.sm,
    gap: spacing.xs,
  },
}));

const InfoTitle = styled(Typography)({
  fontSize: typography.fontSize.sm,
  fontWeight: typography.fontWeight.semibold,
  color: colors.text.primary,
  marginBottom: spacing.xs,
});

const StepsList = styled('ul')({
  listStyle: 'none',
  padding: 0,
  margin: 0,
  '& li': {
    padding: '3px 0',
    fontSize: typography.fontSize.xs,
    color: colors.text.muted,
    '&:before': {
      content: '"\\2713"',
      color: colors.success.main,
      fontWeight: typography.fontWeight.bold,
      marginLeft: spacing.xs,
      fontSize: typography.fontSize.xs,
    },
  },
});

/**
 * Completion screen component with celebration and next steps
 * @param {Object} props
 * @param {string} props.phoneNumber - Phone number value
 * @param {Function} props.onPhoneChange - Phone number change handler
 * @param {Function} props.onSubmit - Submit handler
 * @param {boolean} props.isSubmitting - Whether submission is in progress
 */
export default function CompletionScreen({ phoneNumber, onPhoneChange, onSubmit, isSubmitting }) {
  const { t } = useTranslation();

  return (
    <ScreenContainer>
      <CelebrationIcon>
        <CheckCircleIcon />
      </CelebrationIcon>

      <Title>{t('completionScreen.title', 'סיימת למלא את הטופס!')}</Title>

      <Subtitle>
        {t(
          'completionScreen.subtitle',
          'הזן מספר טלפון לשליחת המסמך'
        )}
      </Subtitle>

      <Card>
        <PhoneInputWrapper>
          <PhoneLabel>
            {t('completionScreen.phoneLabel', 'מספר טלפון נייד')}
          </PhoneLabel>
          <PhoneInput
            type="tel"
            value={phoneNumber}
            onChange={onPhoneChange}
            placeholder="05X-XXXXXXX"
            dir="ltr"
            disabled={isSubmitting}
          />
        </PhoneInputWrapper>

        <SubmitButton
          onClick={onSubmit}
          disabled={phoneNumber.length < 9 || isSubmitting}
          fullWidth
        >
          {isSubmitting
            ? t('completionScreen.submitting', 'שולח...')
            : t('completionScreen.submit', 'שלח מסמך')}
        </SubmitButton>
      </Card>

      <InfoBox>
        <InfoOutlinedIcon sx={{ color: colors.text.muted, flexShrink: 0, fontSize: '18px' }} />
        <Box>
          <InfoTitle>{t('completionScreen.whatHappensNext', 'מה קורה אחר כך?')}</InfoTitle>
          <StepsList>
            <li>{t('completionScreen.step1', 'המסמך החתום יישמר במערכת')}</li>
            <li>{t('completionScreen.step2', 'תקבל אישור להגשה מוצלחת')}</li>
            <li>{t('completionScreen.step3', 'תוכל להוריד עותק חתום')}</li>
          </StepsList>
        </Box>
      </InfoBox>
    </ScreenContainer>
  );
}
